import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { installEmbedBridge } from '@/lib/embedBridge'

/**
 * Mounts the embed bridge (F-FE-N210) inside the query provider so a login
 * handed in by the multi-brand terminal refreshes the cache the same way the
 * login page does (cancel, then invalidate — never remove). Renders nothing
 * and installs nothing unless this document is framed.
 */
export function EmbedBridge() {
  const queryClient = useQueryClient()
  useEffect(
    () =>
      installEmbedBridge({
        isAuthenticated: () => apiClient.isAuthenticated(),
        role: () => storedRole(),
        async login(username, password) {
          try {
            const res = await apiClient.login({ username, password })
            if (!res.success || !res.data) return { ok: false, error: res.error || res.message || 'login_failed' }
            apiClient.setAuth(res.data.token, res.data.user)
            await queryClient.cancelQueries()
            void queryClient.invalidateQueries()
            return { ok: true, role: res.data.user.role }
          } catch (e) {
            const err = e as { response?: { data?: { error?: string; message?: string } }; message?: string }
            return { ok: false, error: err.response?.data?.error || err.response?.data?.message || err.message || 'login_failed' }
          }
        },
        logout() {
          apiClient.clearAuth()
          window.location.assign('/login')
        },
        navigateHome() {
          // Full navigation: the index route sends each role to its landing
          // screen (counter / manager -> the counter), exactly like the login page.
          window.location.assign('/')
        },
      }),
    [queryClient],
  )
  return null
}

function storedRole(): string | undefined {
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return undefined
    const role = (JSON.parse(raw) as { role?: unknown }).role
    return typeof role === 'string' ? role : undefined
  } catch {
    return undefined
  }
}
